/**
 * Firestore parsers for scenario runs and steps (backend-written). Defensive defaults so a
 * malformed or older doc never crashes a listener.
 */

import type { DocumentData, Timestamp } from 'firebase/firestore';
import type {
  QuizAnswer,
  ScenarioContextChange,
  ScenarioContextEntry,
  ScenarioHistoryEntry,
  ScenarioPersonChange,
  ScenarioReview,
  ScenarioRun,
  ScenarioRunStatus,
  ScenarioRunStep,
  ScenarioRunStepSummary,
  ScenarioStepStatus,
} from '../types';
import { parseQuestions } from './parse';
import { isScenarioRole } from './scenario';

const RUN_STATUSES: ScenarioRunStatus[] = ['assigned', 'in_progress', 'completed', 'verified', 'cancelled'];
const STEP_STATUSES: ScenarioStepStatus[] = ['locked', 'open', 'answered', 'verified', 'redo', 'cancelled'];

const ts = (v: unknown): Timestamp | null => (v && typeof v === 'object' ? (v as Timestamp) : null);
const str = (v: unknown, fallback = ''): string => (typeof v === 'string' ? v : fallback);
const strOrNull = (v: unknown): string | null => (typeof v === 'string' && v ? v : null);
const num = (v: unknown, fallback = 0): number => (typeof v === 'number' && Number.isFinite(v) ? v : fallback);
const obj = (v: unknown): Record<string, unknown> =>
  v && typeof v === 'object' && !Array.isArray(v) ? (v as Record<string, unknown>) : {};

function stepStatus(v: unknown): ScenarioStepStatus {
  return STEP_STATUSES.includes(v as ScenarioStepStatus) ? (v as ScenarioStepStatus) : 'locked';
}

function stringMap(v: unknown): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [k, val] of Object.entries(obj(v))) if (typeof val === 'string') out[k] = val;
  return out;
}

function answersOrNull(v: unknown): Record<string, QuizAnswer> | null {
  if (!v || typeof v !== 'object' || Array.isArray(v)) return null;
  const out: Record<string, QuizAnswer> = {};
  for (const [k, val] of Object.entries(v as Record<string, unknown>)) {
    if (typeof val === 'string') out[k] = val;
    else if (Array.isArray(val)) out[k] = val.filter((x): x is string => typeof x === 'string');
  }
  return out;
}

function parseReview(v: unknown): ScenarioReview | null {
  const r = obj(v);
  if (r.decision !== 'verified' && r.decision !== 'redo') return null;
  return { decision: r.decision, note: str(r.note), byEmail: str(r.byEmail), byName: str(r.byName), at: ts(r.at) };
}

function parsePersonChanges(v: unknown): ScenarioPersonChange[] {
  if (!Array.isArray(v)) return [];
  return v.map((raw) => {
    const c = obj(raw);
    return {
      fromEmail: str(c.fromEmail),
      fromName: str(c.fromName) || str(c.fromEmail),
      toEmail: str(c.toEmail),
      toName: str(c.toName) || str(c.toEmail),
      byEmail: str(c.byEmail),
      byName: str(c.byName) || str(c.byEmail),
      at: ts(c.at),
    };
  });
}

function parseContext(v: unknown): Record<string, ScenarioContextEntry> {
  const out: Record<string, ScenarioContextEntry> = {};
  for (const [key, raw] of Object.entries(obj(v))) {
    const e = obj(raw);
    out[key] = {
      value: str(e.value),
      label: str(e.label, key) || key,
      fromStep: num(e.fromStep),
      fromQuestionId: str(e.fromQuestionId),
    };
  }
  return out;
}

function parseContextChanges(v: unknown): Record<string, ScenarioContextChange> {
  const out: Record<string, ScenarioContextChange> = {};
  for (const [key, raw] of Object.entries(obj(v))) {
    const e = obj(raw);
    out[key] = {
      label: str(e.label, key) || key,
      from: str(e.from),
      to: str(e.to),
      fromStep: num(e.fromStep),
      changedAt: ts(e.changedAt),
    };
  }
  return out;
}

function parseSummary(v: unknown, i: number): ScenarioRunStepSummary {
  const s = obj(v);
  return {
    index: num(s.index, i),
    slotId: str(s.slotId),
    slotLabel: str(s.slotLabel),
    assigneeEmail: str(s.assigneeEmail),
    assigneeName: str(s.assigneeName) || str(s.assigneeEmail),
    status: stepStatus(s.status),
    answeredAt: ts(s.answeredAt),
    reviewedByName: strOrNull(s.reviewedByName),
    reviewedAt: ts(s.reviewedAt),
    contextStale: s.contextStale === true,
  };
}

/** reviewerEmail / reviewerName; runs made before reviewers existed fall back to the owner. */
function parseReviewer(data: DocumentData): { reviewerEmail: string; reviewerName: string } {
  const email = str(data.reviewerEmail);
  if (!email) return { reviewerEmail: str(data.ownerEmail), reviewerName: str(data.ownerName) || str(data.ownerEmail) };
  return { reviewerEmail: email, reviewerName: str(data.reviewerName) || email };
}

export function parseScenarioRun(id: string, data: DocumentData): ScenarioRun {
  return {
    id,
    quizId: str(data.quizId),
    quizTitle: str(data.quizTitle),
    locationId: str(data.locationId),
    department: str(data.department, 'parts') || 'parts',
    ownerEmail: str(data.ownerEmail),
    ownerName: str(data.ownerName) || str(data.ownerEmail),
    ...parseReviewer(data),
    reviewerChanges: parsePersonChanges(data.reviewerChanges),
    variables: stringMap(data.variables),
    slots: Array.isArray(data.slots)
      ? data.slots
          .filter((s: DocumentData) => typeof s?.id === 'string' && isScenarioRole(s?.role))
          .map((s: DocumentData) => ({ id: s.id, role: s.role, label: str(s.label) }))
      : [],
    status: RUN_STATUSES.includes(data.status) ? data.status : 'assigned',
    steps: Array.isArray(data.steps) ? data.steps.map(parseSummary) : [],
    createdAt: ts(data.createdAt),
    updatedAt: ts(data.updatedAt),
    completedAt: ts(data.completedAt),
    verifiedAt: ts(data.verifiedAt),
    cancelledByName: strOrNull(data.cancelledByName),
    cancelledAt: ts(data.cancelledAt),
  };
}

export function parseScenarioRunStep(id: string, data: DocumentData): ScenarioRunStep {
  const notice = obj(data.lastNotice);
  return {
    id,
    scenarioId: str(data.scenarioId),
    quizId: str(data.quizId),
    quizTitle: str(data.quizTitle),
    locationId: str(data.locationId),
    ownerEmail: str(data.ownerEmail),
    ownerName: str(data.ownerName) || str(data.ownerEmail),
    ...parseReviewer(data),
    index: num(data.index),
    stepCount: num(data.stepCount, 1),
    slotId: str(data.slotId),
    slotLabel: str(data.slotLabel),
    role: isScenarioRole(data.role) ? data.role : null,
    assigneeEmail: str(data.assigneeEmail),
    assigneeName: str(data.assigneeName) || str(data.assigneeEmail),
    visibleTo: strOrNull(data.visibleTo),
    status: stepStatus(data.status),
    questions: parseQuestions(data.questions),
    variables: stringMap(data.variables),
    context: parseContext(data.context),
    contextChanges: parseContextChanges(data.contextChanges),
    answers: answersOrNull(data.answers),
    answeredAt: ts(data.answeredAt),
    answeredBy: strOrNull(data.answeredBy),
    review: parseReview(data.review),
    history: Array.isArray(data.history)
      ? data.history.map(
          (h: DocumentData): ScenarioHistoryEntry => ({
            answers: answersOrNull(h?.answers),
            answeredAt: ts(h?.answeredAt),
            answeredBy: strOrNull(h?.answeredBy),
            review: parseReview(h?.review),
          })
        )
      : [],
    reassignments: parsePersonChanges(data.reassignments),
    openedAt: ts(data.openedAt),
    lastNotice: data.lastNotice ? { at: ts(notice.at), pushed: notice.pushed === true } : null,
    cancelledByName: strOrNull(data.cancelledByName),
    cancelledAt: ts(data.cancelledAt),
  };
}
